import type { EntityProfileModule } from '../types';
import { StartupProfileSections } from './StartupProfileSections';
import {
  fetchStartupProfile,
  saveStartupProfile,
  type StartupProfileData,
} from '../api/startup-profile.api';
import { toApiPayload, toFormValues } from './startup/schema';

/**
 * Registry-contract adapter: `EntityProfileModule<T>.save` expects the same
 * shape in and out, but GET and PATCH have genuinely different shapes for
 * startups (nested `sectors[]` vs. flat `sectorId`/`subsectorIds`,
 * `pitchDeckUrl` vs. `pitchDeckFileKey`, no `id`/`entityId` on write). This
 * slot is never actually invoked - `StartupProfileSections` (MYN-72's edit
 * flow) calls `useSaveStartupProfile` -> `saveStartupProfile` directly with
 * the narrower `StartupProfileUpdatePayload` instead - but it still has to
 * satisfy the shared interface, so it delegates to the exact same
 * `toFormValues`/`toApiPayload` round-trip the real edit flow uses (both
 * already covered by `schema.test.ts`) rather than re-deriving the GET->PATCH
 * mapping by hand a second time (a prior version hand-duplicated it here,
 * flagged by pr-review as untested and drift-prone).
 */
function saveForRegistry(entityId: string, data: StartupProfileData): Promise<StartupProfileData> {
  return saveStartupProfile(entityId, toApiPayload(toFormValues(data), data));
}

export const startupProfileModule: EntityProfileModule<StartupProfileData> = {
  status: 'ready',
  queryKey: (entityId) => ['profile', 'startup', entityId],
  fetch: fetchStartupProfile,
  save: saveForRegistry,
  Sections: StartupProfileSections,
};
